import { getFormattedGoogleAddress } from '../../../services/address.service';
import { type AddressI18nFormScheme } from '../../Address/types/addressInternationalisationTypes';

type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean
) => void | Promise<unknown>;

export interface HandleAddressFormChangeArgs {
  readonly result: google.maps.places.PlaceResult;
  readonly languageCode: string;
  readonly setIsInSearchMode: (value: boolean) => void;
  readonly setFieldValue: SetFieldValue;
  readonly setCoords: (coords: google.maps.LatLngLiteral) => void;
  readonly setCenter: (coords: google.maps.LatLngLiteral) => void;
  readonly setCountryCode: (value: string) => void;
}

export const handleAddressFormChange = async ({
  result,
  languageCode,
  setIsInSearchMode,
  setFieldValue,
  setCoords,
  setCenter,
  setCountryCode,
}: HandleAddressFormChangeArgs): Promise<void> => {
  setIsInSearchMode(false);

  // Start an API call to the V1 API here, but we can move ahead with other work
  // before we await the response - we can update the property name, phone numbers,
  // and coordinates immediately.
  const addressPromise: Promise<AddressI18nFormScheme> = getFormattedGoogleAddress(
    result,
    google.maps.GeocoderStatus.OK,
    languageCode
  );

  await setFieldValue('name', result.name);
  await setFieldValue('phoneNumber', result.international_phone_number?.replaceAll(' ', ''));

  const newCoords = result.geometry?.location?.toJSON();
  if (newCoords) {
    // Google Autocomplete gave us coordinates

    // Update the form with the new coordinates
    await setFieldValue('coordinates', {
      latitude: newCoords.lat,
      longitude: newCoords.lng,
    });

    // Update the map widget with the new coordinates
    setCoords(newCoords);
    setCenter(newCoords);
  }

  // Now we can await the Zeus API call, and fill in the address fields
  const address = await addressPromise;

  if (address.CountryCode) {
    await setFieldValue('countryCode', address.CountryCode);
    setCountryCode(address.CountryCode);
  }

  if (address.FormData) {
    await Promise.all(
      address.FormData.map(async (field) => {
        await setFieldValue(`addressFields.${field.Identifier}`, field.Value);
      })
    );
  }
};
